import React, { useEffect, useState } from 'react'

import { Button, Card, CardContent, CardHeader } from '@mui/material'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import type { RootState } from 'reducers'

import { addPaused, addRunning, clearSelection, exportCampaignTargets } from 'actions/campaign-targets-actions'
import { Flag } from 'components/_base'
import { Table } from 'components/table'
import { useAppDispatch } from 'hooks/useAppDispatch'
import { useCampaignTargets } from 'hooks/useCampaignTargets'
import { usePaginatedTableMeta } from 'hooks/usePaginatedTableMeta'

import CampaignTargetActionButton from './CampaignTargetActionButton'
import CampaignTargetsFilters from './CampaignTargetsFilters'
import CampaignTargetsMatch from './CampaignTargetsMatch'
import CampaignTargetsModal from './CampaignTargetsModal'

const INITIAL_FILTERS = { status: '', query: '' }

const CampaignTargets = () => {
  const { id } = useParams()
  const [modal, setModal] = useState<string | null>('')
  const { meta, setPage, setRowsPerPage, setFilters } = usePaginatedTableMeta({
    filters: INITIAL_FILTERS,
  })
  const { data, isLoading, error } = useCampaignTargets(id, meta)
  const dispatch = useAppDispatch()
  const updates = useSelector((s: RootState) => s.campaignTargets.meta.get('updates'))

  // Clear the running/paused selection when leaving the tab (was resetMeta on unmount).
  useEffect(
    () => () => {
      dispatch(clearSelection())
    },
    [dispatch],
  )

  const handleModalOpen = (m: string) => () => setModal(m)
  const handleModalClose = () => setModal(null)

  const handleReset = () => {
    setFilters(INITIAL_FILTERS)
    dispatch(clearSelection())
  }

  const renderActionButton = (rowData: any) => (
    <CampaignTargetActionButton
      addPaused={(...args: any[]) => dispatch(addPaused(...args))}
      addRunning={(...args: any[]) => dispatch(addRunning(...args))}
      data={rowData}
      updates={updates}
    />
  )

  const config = [
    { title: 'Country', render: (rowData: any) => <Flag country={rowData.country} /> },
    { title: 'Name', field: 'name' },
    { title: 'Status', field: 'status' },
    { title: 'IPs', field: 'ranges' },
    { title: 'UUID', field: 'companyUuid' },
    { title: 'Action', render: renderActionButton },
  ]

  return (
    <div>
      <Card>
        <CardHeader
          action={
            <div>
              <Button onClick={handleModalOpen('upload')}>Add new</Button>
              <Button onClick={() => dispatch(exportCampaignTargets(id))}>Export</Button>
              <Button onClick={handleReset}>Reset</Button>
              <Button color="primary" onClick={handleModalOpen('update')}>
                Update
              </Button>
            </div>
          }
          title="Targets"
        />
        <CardContent>
          <Table
            config={config}
            count={data?.count}
            data={data?.items}
            error={error ? 'Error occurred when loading campaign targets data' : undefined}
            loading={isLoading}
            onChangePage={setPage}
            onChangeRowsPerPage={setRowsPerPage}
            page={meta.page}
            rowsPerPage={meta.rowsPerPage}
            toolbar={<CampaignTargetsFilters onFilterChange={setFilters} />}
          />
        </CardContent>
      </Card>

      <CampaignTargetsMatch id={id} onClose={handleModalClose} open={modal === 'upload'} />
      <CampaignTargetsModal id={id ?? ''} onClose={handleModalClose} open={modal === 'update'} />
    </div>
  )
}

export default CampaignTargets
